import { useState, useEffect } from "react";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import { Plus, Trash2, ArrowDown, Lightbulb } from "lucide-react";

interface FiveWhyData {
  problem: string;
  whys: string[];
  rootCause: string;
}

interface FiveWhyAnalysisProps {
  data: FiveWhyData;
  onChange: (data: FiveWhyData) => void;
}

export function FiveWhyAnalysis({ data, onChange }: FiveWhyAnalysisProps) {
  const [localData, setLocalData] = useState<FiveWhyData>(data);

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const updateData = (updates: Partial<FiveWhyData>) => {
    const newData = { ...localData, ...updates };
    setLocalData(newData);
    onChange(newData);
  };

  const updateWhy = (index: number, value: string) => {
    const newWhys = [...localData.whys];
    newWhys[index] = value;
    updateData({ whys: newWhys });
  };

  const addWhy = () => {
    if (localData.whys.length < 7) {
      updateData({ whys: [...localData.whys, ""] });
    }
  };

  const removeWhy = (index: number) => {
    if (localData.whys.length > 1) {
      const newWhys = localData.whys.filter((_, i) => i !== index);
      updateData({ whys: newWhys });
    }
  };

  return (
    <div className="space-y-4">
      {/* Problem Statement */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground">
          Problem Statement
        </label>
        <Input
          placeholder="What is the problem?"
          value={localData.problem}
          onChange={(e) => updateData({ problem: e.target.value })}
          className="bg-destructive/10 border-destructive/30 focus:border-destructive"
        />
      </div>

      {/* Why Chain */}
      <div className="space-y-2 pl-4 border-l-2 border-primary/30">
        {localData.whys.map((why, index) => (
          <div key={index} className="space-y-2">
            <div className="flex items-center gap-2">
              <ArrowDown className="h-4 w-4 text-primary flex-shrink-0" />
              <span className="text-sm font-medium text-primary">
                Why #{index + 1}
              </span>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder={`Why ${index === 0 ? "did this happen" : "is that"}?`}
                value={why}
                onChange={(e) => updateWhy(index, e.target.value)}
                className="flex-1"
              />
              {localData.whys.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 text-muted-foreground hover:text-destructive"
                  onClick={() => removeWhy(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}

        {localData.whys.length < 7 && (
          <Button
            variant="outline"
            size="sm"
            className="ml-6 border-dashed"
            onClick={addWhy}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Why
          </Button>
        )}
      </div>

      {/* Root Cause */}
      <div className="space-y-2 pt-2">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-warning" />
          <label className="text-sm font-medium text-warning">
            Root Cause
          </label>
        </div>
        <Input
          placeholder="The root cause is..."
          value={localData.rootCause}
          onChange={(e) => updateData({ rootCause: e.target.value })}
          className="bg-warning/10 border-warning/30 focus:border-warning"
        />
      </div>

      {/* Progress indicator */}
      <div className="flex items-center gap-2 pt-2 text-xs text-muted-foreground">
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <div
              key={n}
              className={`w-6 h-1.5 rounded-full transition-colors ${
                localData.whys.length >= n && localData.whys[n - 1]?.trim()
                  ? "bg-primary"
                  : "bg-muted"
              }`}
            />
          ))}
        </div>
        <span>
          {localData.whys.filter((w) => w.trim()).length} of 5 whys completed
        </span>
      </div>
    </div>
  );
}
